import { Component, Input, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProgressbarComponent } from '../progressbar/progressbar.component';
import { HttpClient, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../../../projects/my-app/src/environments/environment.dev';

interface TableRow {
  process: string;
  entityName: string;
  triggerSource: string;
  totalOffset: number;
  currentOffset: number;
  updatedDateTime: string;
  updatedBy: string;
  graph?: string;
}

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  imports: [CommonModule, FormsModule, ProgressbarComponent, HttpClientModule],
  providers: [DatePipe],
  standalone: true
})
export class ViewComponent implements OnInit {
  rows: TableRow[] = [];

  // Dropdown data
  processesOptions: string[] = [];
  entityNameOptions: string[] = [];
  triggerSourceOptions: string[] = [];

  // Selected filter values
  selectedProcess: string = '';
  selectedTriggerSource: string = '';
  selectedEntityName: string = '';

  // Unique arrays (built from table rows)
  uniqueProcesses: string[] = [];
  uniqueTriggerSources: string[] = [];
  uniqueEntityNames: string[] = [];

  process_url = environment.const_url_process;

  @Input() data: any;

  token: string | null = null;

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService
  ) {}

  // ---- KEYCLOAK ----
  private async initializeKeycloak(): Promise<void> {
    this.token = await this.keycloakService.getToken();
    console.log('Keycloak token:', this.token);
  }

  // ---- LIFECYCLE ----
  async ngOnInit(): Promise<void> {
    await this.initializeKeycloak();

    // 1) Load table data first (so we have entityName / triggerSource values)
    await this.loadDataFromApi();

    // 2) Then load filter options (optional override)
    const filterApiUrl = this.process_url + this.data?.filters?.apiUrl;
    if (filterApiUrl) {
      await this.loadFiltersFromApi(filterApiUrl);
    }
  }

  // ---- API CALLS ----

  // Load table data (POST with filters) and build unique lists + default dropdowns
  async loadDataFromApi(): Promise<void> {
    const dataApiUrl = this.process_url + this.data?.dataApiUrl;

    // Build payload dynamically (only include non-empty filters)
    const payload: any = {};
    if (this.selectedProcess) payload.process = this.selectedProcess;
    if (this.selectedEntityName) payload.entityName = this.selectedEntityName;
    if (this.selectedTriggerSource) payload.triggerSource = this.selectedTriggerSource;

    console.log('Sending payload to backend:', payload);

    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (this.token) {
      headers = headers.set('Authorization', `Bearer ${this.token}`);
    }

    console.log('token:', this.token);
    console.log('headers:', headers);

    return new Promise<void>((resolve, reject) => {
      this.http.post<any>(dataApiUrl, payload, { headers }).subscribe({
        next: (data) => {
          console.log('Raw table data from API:', data);
          const extracted = this.extractFirstArray(data);
          console.log('Extracted array:', extracted);

          this.rows = this.normalizeRows(extracted);
          console.log('Normalized rows:', this.rows);

          // Build unique values from table rows
          this.uniqueProcesses = [...new Set(this.rows.map(r => r.process).filter(Boolean))];
          this.uniqueEntityNames = [...new Set(this.rows.map(r => r.entityName).filter(Boolean))];
          this.uniqueTriggerSources = [...new Set(this.rows.map(r => r.triggerSource).filter(Boolean))];

          console.log('Unique Processes:', this.uniqueProcesses);
          console.log('Unique Entity Names:', this.uniqueEntityNames);
          console.log('Unique Trigger Sources:', this.uniqueTriggerSources);

          // Default dropdowns from table data
          if (!this.processesOptions.length) {
            this.processesOptions = [...this.uniqueProcesses];
          }
          if (!this.entityNameOptions.length) {
            this.entityNameOptions = [...this.uniqueEntityNames];
          }
          if (!this.triggerSourceOptions.length) {
            this.triggerSourceOptions = [...this.uniqueTriggerSources];
          }

          resolve();
        },
        error: (err) => {
          console.error('Failed to load table data', err);
          resolve(); // don’t block UI even if it fails
        }
      });
    });
  }

  // Trigger reload on dropdown change
  async onFilterChange(): Promise<void> {
    console.log('Filters changed:', {
      process: this.selectedProcess,
      entityName: this.selectedEntityName,
      triggerSource: this.selectedTriggerSource
    });
    await this.loadDataFromApi();
  }

  // Load filter dropdowns from filter API (optional) and override default options if present
  async loadFiltersFromApi(url: string): Promise<void> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (this.token) {
      headers = headers.set('Authorization', `Bearer ${this.token}`);
    }
    console.log('token inside loadFiltersFromApi:', this.token);

    return new Promise<void>((resolve, reject) => {
      this.http.get<any>(url, { headers }).subscribe({
        next: (res) => {
          console.log('Filter API Response:', res);

          const apiProcesses =
            Array.isArray(res?.process) && res.process.length
              ? res.process
              : (Array.isArray(res?.['Current Processes']) && res['Current Processes'].length
                  ? res['Current Processes']
                  : null);

          const apiEntityNames =
            Array.isArray(res?.entityName) && res.entityName.length
              ? res.entityName
              : (Array.isArray(res?.['Entity Name']) && res['Entity Name'].length
                  ? res['Entity Name']
                  : null);

          const apiTriggerSources =
            Array.isArray(res?.triggerSource) && res.triggerSource.length
              ? res.triggerSource
              : (Array.isArray(res?.['Trigger Source']) && res['Trigger Source'].length
                  ? res['Trigger Source']
                  : null);

          // If API gives valid data, override; otherwise keep table-derived defaults
          if (apiProcesses) {
            this.processesOptions = apiProcesses;
          }
          if (apiEntityNames) {
            this.entityNameOptions = apiEntityNames;
          }
          if (apiTriggerSources) {
            this.triggerSourceOptions = apiTriggerSources;
          }

          console.log('processesOptions:', this.processesOptions);
          console.log('entityNameOptions:', this.entityNameOptions);
          console.log('triggerSourceOptions:', this.triggerSourceOptions);

          resolve();
        },
        error: (err) => {
          console.error('Failed to load filter options', err);
          resolve(); // ignore failure, keep table-based options
        }
      });
    });
  }

  // ---- HELPERS ----

  // Extract the first array from any API response shape
  private extractFirstArray(response: any): any[] {
    if (!response) return [];

    if (Array.isArray(response)) return response;

    const candidates = [
      response.data,
      response.rows,
      response.result,
      response.content,
      response.items,
      response.payload
    ].filter((v) => Array.isArray(v))[0];

    if (candidates) return candidates;

    if (typeof response === 'object') {
      const values = Object.values(response);
      for (const v of values) {
        if (Array.isArray(v)) return v;
        if (v && typeof v === 'object') {
          const innerValues = Object.values(v as any);
          const innerArray = innerValues.find((iv) => Array.isArray(iv));
          if (innerArray && Array.isArray(innerArray)) return innerArray as any[];
        }
      }
      if (values.every((vv) => typeof vv === 'object')) return values as any[];
    }

    return [];
  }

  // Normalize backend data fields to UI model
  private normalizeRows(input: any[]): TableRow[] {
    return (input || []).map((item: any) => {
      const process =
        item?.process ??
        item?.currentProcess ??
        item?.CURRENT_PROCESS ??
        item['CURRENT PROCESS'];

      const entityName =
        item?.entityName ??
        item?.entity_name ??
        item?.ENTITY_NAME ??
        item['ENTITY NAME'];

      const triggerSource =
        item?.triggerSource ??
        item?.trigger_source ??
        item?.TRIGGER_SOURCE ??
        item['TRIGGER SOURCE'];

      const totalOffsetRaw = item?.totalOffset ?? item?.TOTAL_OFFSET;
      const currentOffsetRaw = item?.currentOffset ?? item?.CURRENT_OFFSET;

      const totalOffset = Number.isFinite(Number(totalOffsetRaw)) ? Number(totalOffsetRaw) : 0;
      const currentOffset = Number.isFinite(Number(currentOffsetRaw)) ? Number(currentOffsetRaw) : 0;

      const updatedDateTime = item?.updatedDateTime ?? item?.UPDATED_DATE_TIME;
      const updatedBy = item?.updatedBy ?? item?.UPDATED_BY;

      return {
        process,
        entityName,
        triggerSource,
        totalOffset,
        currentOffset,
        updatedDateTime,
        updatedBy
      } as TableRow;
    });
  }

  // Client-side filtering (fallback / additional safeguard)
  get filteredRows(): TableRow[] {
    return this.rows.filter((row) => {
      let matches = true;

      if (this.selectedProcess) {
        matches = matches && row.process === this.selectedProcess;
      }
      if (this.selectedTriggerSource) {
        matches = matches && row.triggerSource === this.selectedTriggerSource;
      }
      if (this.selectedEntityName) {
        matches = matches && row.entityName === this.selectedEntityName;
      }

      return matches;
    });
  }

  trackByValue(index: number, value: any): any {
    return value;
  }
}
